import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    DataGrid,
    GridColDef,
    GridRowParams,
    GridFooter,
    GridFooterContainer,
    GridRenderCellParams,
    GridComparatorFn,
} from '@mui/x-data-grid';
import { Button, IconButton, LinearProgress, Link } from '@mui/material';
import {
    endOfDay,
    format,
    isAfter,
    isBefore,
    isSameDay,
    isValid,
    startOfDay,
} from 'date-fns';
import { Ticket } from 'types/User/firebaseTypes/ticket';
import { makeDateConsistent } from 'helpers/dates';
import { useUi } from 'hooks/useUi';
import { DataGridPlaceholder } from 'components/DataGridPlaceholder/DataGridPlaceholder';
import styles from './DeliveriesListView.module.scss';
import { useLazyUpdateDeliveryTicketQuery } from 'store/services/planning';
import { StatusSelector } from 'views/Planning/common/StatusSelector';
import classNames from 'classnames';
import { convertDeliveryStatusToInternalName } from 'lib/constants';
import { CreatedBy } from 'components/common/CreatedBy/CreatedBy';
import { isEmpty } from 'lodash';
import { DeviceLookup } from 'views/Planning/Planning';
import { IssueCauseIcon } from 'components/common/IssueCauseIcon/IssueCauseIcon';

const dateComparator: GridComparatorFn<Date | string | number> = (v1, v2) => {
    const date1 = new Date(v1);
    const date2 = new Date(v2);

    return isAfter(date1, date2) ? 1 : -1;
};

export const DeliveriesListView: FC<{
    deliveries: Array<Ticket>;
    supplierId: string | null;
    selectedTickets: Array<string>;
    minDate: Date;
    maxDate: Date;
    setSelectedTickets: (a: Array<string>) => void;
    setActiveTicket: (t: Ticket | null) => void;
    setIsDetailsModalOpen: (o: boolean) => void;
    setIsBulkModalOpen: (o: boolean) => void;
    setAllDeliveries: (
        d: Ticket[] | ((deliveries: Ticket[]) => Ticket[])
    ) => void;
    devicesList: DeviceLookup;
    isLoading?: boolean;
    onDoneSelected: (t: Ticket) => void;
}> = ({
    deliveries,
    supplierId,
    selectedTickets,
    minDate,
    maxDate,
    setSelectedTickets,
    setActiveTicket,
    setIsDetailsModalOpen,
    setIsBulkModalOpen,
    setAllDeliveries,
    devicesList,
    isLoading,
    onDoneSelected,
}) => {
    const { ui } = useUi();
    const navigate = useNavigate();
    const columns: GridColDef[] = [
        {
            field: 'device.customer_name',
            headerName: 'Customer',
            flex: 1,
            minWidth: 125,
            sortingOrder: ['asc', 'desc'],
            valueGetter: (param) => {
                if (isEmpty(devicesList)) {
                    return '...';
                }

                return (
                    devicesList[param.row.deviceId]?.customer_name ??
                    param.row?.supplierName ??
                    '-'
                );
            },
            cellClassName: () =>
                isEmpty(devicesList) ? styles.deviceLoading : '',
        },
        {
            field: 'device.name',
            headerName: 'Monitor',
            flex: 1,
            minWidth: 125,
            sortingOrder: ['asc', 'desc'],
            valueGetter: (params) => {
                if (params.row?.name) {
                    return params.row?.name;
                }

                if (isEmpty(devicesList)) {
                    return '...';
                }

                const device = devicesList[params.row?.deviceId];

                return device?.name ?? device?.shortDeviceId ?? '-';
            },
            renderCell: (params) => {
                if (isEmpty(devicesList)) {
                    return '...';
                }

                const device = devicesList[params.row?.deviceId];

                if (isEmpty(devicesList)) {
                    return '...';
                }

                return (
                    <div className={styles.deviceName}>
                        <Link
                            title={
                                device?.name ??
                                device?.shortDeviceId ??
                                'Unknown Monitor'
                            }
                        >
                            {device?.name ?? device?.shortDeviceId ?? '-'}
                        </Link>
                        {device?.activeIssueTicket && (
                            <IconButton
                                sx={{ padding: 0 }}
                                onClick={(e) => {
                                    e.stopPropagation();

                                    navigate(
                                        `/device-details/${device?.deviceId}/issues`,
                                        {
                                            state: device,
                                        }
                                    );
                                }}
                            >
                                <IssueCauseIcon
                                    trigger={
                                        device.activeIssueTicket?.ticketTrigger
                                    }
                                    fontSize="small"
                                    color="warning"
                                />
                            </IconButton>
                        )}
                    </div>
                );
            },
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 1,
            minWidth: 125,
            sortingOrder: ['asc', 'desc'],
            renderCell: (params: GridRenderCellParams) => (
                <StatusCell
                    cellParams={params}
                    ticket={params.row}
                    supplierId={supplierId}
                    deliveries={deliveries}
                    setAllDeliveries={setAllDeliveries}
                    onDoneSelected={onDoneSelected}
                />
            ),
            type: 'actions',
        },
        {
            field: 'estimatedDelivery',
            headerName: 'Estimated Delivery',
            flex: 1,
            minWidth: 125,
            sortingOrder: ['asc', 'desc'],
            valueGetter: (param) => {
                if (isEmpty(devicesList)) {
                    return '...';
                }

                const device = devicesList[param.row?.deviceId];
                const estDate = device?.duf_7;
                if (estDate && isValid(estDate)) {
                    return format(estDate, 'MM/dd/yyyy');
                }

                return '-';
            },
            cellClassName: () =>
                isEmpty(devicesList) ? styles.deviceLoading : '',
        },
        {
            field: 'plannedDate',
            headerName: 'Date Planned',
            flex: 1,
            minWidth: 125,
            renderCell: DateCell,
            sortingOrder: ['asc', 'desc'],
            sortComparator: dateComparator,
            valueGetter: (param) => {
                const device = devicesList[param.row?.deviceId];

                return param.row?.plannedDate ?? device?.duf_7;
            },
        },
        {
            field: 'dateDelivered',
            headerName: 'Date Delivered',
            flex: 1,
            minWidth: 125,
            sortingOrder: ['asc', 'desc'],
            renderCell: DateCell,
        },
        {
            field: 'plannedGallons',
            headerName: 'Gallons Planned',
            flex: 1,
            minWidth: 125,
            sortingOrder: ['asc', 'desc'],
            sortComparator: dateComparator,
            valueGetter: (param) => {
                return param.row?.plannedGallons
                    ? Number(param.row?.plannedGallons)?.toFixed(2)
                    : '-';
            },
        },
        {
            field: 'gallonsDelivered',
            headerName: 'Gallons Delivered',
            flex: 1,
            minWidth: 125,
            sortingOrder: ['asc', 'desc'],
            valueGetter: (param) => {
                return param.row?.gallonsDelivered
                    ? Number(param.row?.gallonsDelivered)?.toFixed(2)
                    : '-';
            },
        },
        {
            field: 'device.address',
            headerName: 'Address',
            flex: 3,
            minWidth: 125,
            sortingOrder: ['asc', 'desc'],
            valueGetter: (param) => {
                if (isEmpty(devicesList)) {
                    return '...';
                }

                const device = devicesList[param.row?.deviceId];

                return device?.address || '-';
            },
            cellClassName: () =>
                isEmpty(devicesList) ? styles.deviceLoading : '',
        },
        {
            field: 'createdBy',
            headerName: 'Created By',
            flex: 1,
            minWidth: 125,
            align: 'right',
            sortable: false,
            renderCell: (params) => {
                return (
                    <CreatedBy
                        ticket={params.row}
                        showIcon={true}
                        showText={false}
                    />
                );
            },
        },
    ];

    const [filteredDeliveries, setFilteredDeliveries] =
        useState<Array<Ticket>>(deliveries);

    const openDetailsModal = (params: GridRowParams) => {
        setActiveTicket(
            deliveries.find((ticket) => ticket.id === params.id) || null
        );
        setIsDetailsModalOpen(true);
    };

    useEffect(() => {
        const newDeliveryList = deliveries.filter((ticket) => {
            const device = devicesList[ticket.deviceId];
            const plannedDate = ticket?.plannedDate
                ? makeDateConsistent(ticket.plannedDate)
                : makeDateConsistent(device?.duf_7);
            const dateDelivered = makeDateConsistent(ticket?.dateDelivered);

            const plannedInRange = !!plannedDate
                ? isBefore(plannedDate, endOfDay(maxDate)) && // before end of last day specified
                  (isAfter(plannedDate, startOfDay(minDate)) || // or start of first day specified
                      isSameDay(plannedDate, minDate)) // or same as first day specified (date-fns lacks an isSameOrAfter method)
                : false;

            const deliveredInRange = !!dateDelivered
                ? isBefore(dateDelivered, endOfDay(maxDate)) &&
                  (isAfter(dateDelivered, startOfDay(minDate)) ||
                      isSameDay(dateDelivered, minDate))
                : false;

            return plannedInRange || deliveredInRange;
        });

        setFilteredDeliveries(newDeliveryList);
    }, [deliveries, minDate, maxDate, devicesList]);

    return (
        <div className={styles.container} data-testid="DeliveriesListView">
            {ui.animatingViewTransition ? (
                <DataGridPlaceholder />
            ) : (
                <DataGrid
                    loading={isLoading}
                    initialState={{
                        columns: {
                            columnVisibilityModel: {
                                // Hide estimated date col by default
                                estimatedDelivery: false,
                            },
                        },
                        sorting: {
                            sortModel: [{ field: 'plannedDate', sort: 'asc' }],
                        },
                    }}
                    columns={columns}
                    rows={filteredDeliveries}
                    getRowId={(delivery) => delivery.id}
                    checkboxSelection
                    disableSelectionOnClick
                    selectionModel={selectedTickets.map((id) => id)}
                    onSelectionModelChange={(idList) =>
                        setSelectedTickets(idList as Array<string>)
                    }
                    onRowClick={openDetailsModal}
                    getRowClassName={(params) =>
                        classNames(styles.tableRow, {
                            [styles.doneRow]:
                                convertDeliveryStatusToInternalName[
                                    params.row?.status
                                ] === 'done',
                        })
                    }
                    onCellClick={(params, e) => {
                        if (!params || params.field !== 'device.name') {
                            return;
                        }

                        e.stopPropagation();
                        e.preventDefault();

                        const device = devicesList[params.row.deviceId];

                        if (!device) {
                            return;
                        }

                        navigate(`/device-details/${device?.deviceId}`, {
                            state: device,
                        });
                    }}
                    className={styles.gridMain}
                    components={{
                        LoadingOverlay: LinearProgress,
                        Footer: () => (
                            <GridFooterContainer>
                                <div
                                    className={`${styles.footerActions} ${
                                        !!selectedTickets.length
                                            ? styles.active
                                            : ''
                                    }`}
                                >
                                    <strong>Bulk Actions:</strong>
                                    <Button
                                        variant="text"
                                        onClick={() => setIsBulkModalOpen(true)}
                                    >
                                        Update Status
                                    </Button>
                                </div>
                                <GridFooter
                                    className={styles.footerPagination}
                                />
                            </GridFooterContainer>
                        ),
                    }}
                    disableColumnMenu
                />
            )}
        </div>
    );
};

const DateCell: FC<GridRenderCellParams> = (props: GridRenderCellParams) => {
    const date = new Date(props?.value);

    if (isValid(date)) {
        return <div>{format(new Date(date), 'MM/dd/yyyy')}</div>;
    }

    return <div>-</div>;
};

const StatusCell: FC<{
    cellParams: GridRenderCellParams;
    ticket: Ticket;
    supplierId: string | null;
    deliveries: Ticket[];
    setAllDeliveries: (
        d: Ticket[] | ((deliveries: Ticket[]) => Ticket[])
    ) => void;
    onDoneSelected: (t: Ticket) => void;
}> = ({
    cellParams,
    ticket,
    supplierId,
    deliveries,
    setAllDeliveries,
    onDoneSelected,
}) => {
    const [status, setStatus] = useState(cellParams?.value || 'backlog');

    const [updateDeliveryQuery] = useLazyUpdateDeliveryTicketQuery();

    useEffect(() => {
        if (status !== cellParams.value) {
            setStatus(cellParams.value);
        }
    }, [status, cellParams.value]);

    const handleChange = (newStatus: string) => {
        setStatus(newStatus);

        if (newStatus === 'done') {
            return onDoneSelected(ticket);
        }

        const updateRequest = updateDeliveryQuery;

        if ((supplierId || supplierId?.toString() === '0') && ticket)
            Promise.resolve(
                updateRequest({
                    supplierId,
                    deviceId: ticket?.deviceId,
                    newTicket: { ...ticket, status: newStatus },
                })
            ).then((result) => {
                if (result.data) {
                    setAllDeliveries((oldDeliveries: Ticket[]) => {
                        const newDeliveries = [...oldDeliveries];

                        const index = deliveries.findIndex(
                            (delivery) => delivery?.id === result.data?.id
                        );

                        if (index > -1 && result.data) {
                            newDeliveries.splice(index, 1, result.data);
                        }

                        return newDeliveries;
                    });
                }
            });
    };

    return (
        <div
            data-testid="DeliveriesListView_StatusCell"
            className={styles.statusCell}
        >
            <StatusSelector
                value={status}
                onChange={(e: string) => handleChange(e)}
            />
        </div>
    );
};

export default DeliveriesListView;
